import { useEffect } from "react";
import { Link } from "react-router-dom";
import { complexMnnkBroMedia, RESUME_URL } from "@/data";

const KEY_CONTRIBUTIONS = [
  "AI environment design & generative visual development",
  "Worldbuilding around supplied MNNK Bro. logos, artwork & talent imagery",
  "Visual consistency across editorial covers, social carousels & motion",
  "Translating brand artwork into chrome cities, diamond moons & flower portals",
  "AI-generated motion assets to extend the portal concept into movement",
  "Art-directing for Complex's editorial visual tone",
];

function GalleryItem({ item }) {
  return (
    <figure className="mnnk-gallery-item" data-testid="mnnk-gallery-item">
      <img src={item.src} alt={item.label} loading="lazy" />
      <figcaption className="mnnk-gallery-caption">{item.label}</figcaption>
    </figure>
  );
}

function InlineVideo({ video, big = false }) {
  return (
    <figure
      className={`mnnk-inline-video ${big ? "mnnk-inline-video-big" : ""}`}
      data-testid="mnnk-inline-video"
    >
      <video
        src={video.src}
        controls
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
      />
      <figcaption className="mnnk-inline-video-caption">
        <span className="mnnk-inline-video-title">{video.title}</span>
        {video.caption}
      </figcaption>
    </figure>
  );
}

export default function ComplexMNNKBro() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -100px 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App thtl-page mnnk-page">
      {/* Top Nav */}
      <div className="cs-topnav" data-testid="cs-topnav">
        <Link to="/" className="cs-back" data-testid="cs-back-link">
          <span className="cs-back-arrow">←</span>
          <span>Dan McNamara</span>
        </Link>
        <div className="nav-links">
          <Link to="/#reel">Reel</Link>
          <Link to="/#case-studies">Case Studies</Link>
          <Link to="/#work">Work</Link>
          <Link to="/#contact">Contact</Link>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-resume"
            data-testid="nav-resume"
          >
            Resume
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="thtl-hero" data-testid="mnnk-hero">
        <div className="thtl-hero-grid">
          <div className="thtl-hero-text">
            <p className="thtl-kicker reveal">
              <span className="thtl-pulse"></span>
              Case Study · Complex
            </p>
            <h1 className="thtl-headline reveal" data-testid="mnnk-headline">
              Complex
              <br />
              × MNNK Bro.
            </h1>
            <p className="thtl-tagline reveal">
              AI-generated environments &amp; motion assets for a surreal
              editorial campaign.
            </p>
            <div className="thtl-divider reveal"></div>
            <p className="thtl-summary reveal">
              For <strong>Complex</strong>'s MNNK Bro. feature — the rap duo
              that includes Takashi Murakami — I helped develop a fully
              AI-generated visual world from supplied logos, artwork, and
              talent references. Chrome cities, diamond moons, flower
              characters, futuristic motorcycles, and dreamlike portals,
              designed to feel like the artists had stepped into Complex's
              version of the MNNK Bro. universe.
            </p>
          </div>

          <div className="thtl-hero-art mnnk-hero-art reveal" aria-hidden="true">
            <img src={complexMnnkBroMedia.hero.src} alt={complexMnnkBroMedia.hero.alt} />
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section className="thtl-meta-strip reveal" data-testid="mnnk-meta-strip">
        <div className="thtl-meta-grid">
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Client</span>
            <span className="thtl-meta-value">Complex</span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Role</span>
            <span className="thtl-meta-value">
              AI Environment Design · Art Direction · Motion
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Subject</span>
            <span className="thtl-meta-value">
              MNNK Bro. (incl. Takashi Murakami)
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Output</span>
            <span className="thtl-meta-value">
              Editorial · Social · Motion
            </span>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section
        id="overview"
        className="thtl-section reveal"
        data-testid="mnnk-overview"
      >
        <div className="thtl-section-header">
          <span className="section-number">01</span>
          <h2 className="thtl-section-title">Overview</h2>
        </div>

        <div className="thtl-process-copy">
          <p className="thtl-lead">
            Complex needed a surreal visual system for an editorial and social
            rollout around MNNK Bro. — the rap duo that includes Takashi
            Murakami — and their collaborative world.
          </p>
          <p>
            The brief wasn't a traditional photoshoot environment — it was a
            heightened, impossible universe. The artists travel through a
            portal into Complex's version of the MNNK Bro. world: chrome
            cityscapes, diamond moons, flower characters, inflated toy-like
            structures, motorcycles, and glossy dreamlike environments.
          </p>
        </div>
      </section>

      {/* Role */}
      <section
        id="role"
        className="thtl-section thtl-role reveal"
        data-testid="mnnk-role"
      >
        <div className="thtl-section-header">
          <span className="section-number">02</span>
          <h2 className="thtl-section-title">Role</h2>
        </div>

        <div className="thtl-role-body">
          <div className="thtl-role-copy">
            <p>
              I helped create the AI-generated environments and visual world
              used across the campaign. Working from supplied logos, artwork,
              character assets, and talent imagery, I generated and refined
              backgrounds, scene concepts, and dreamlike spaces tuned to MNNK
              Bro.'s identity while sitting cleanly inside Complex's editorial
              visual language.
            </p>
            <p>
              In parallel, I created two AI-generated video assets and
              assisted with a third video that brought motion to the cover
              itself. I extended the still campaign into motion — giving the
              portal concept a cinematic sense of movement and atmosphere.
            </p>
          </div>

          <ul
            className="thtl-contributions"
            aria-label="Key contributions"
            data-testid="mnnk-contributions"
          >
            <li className="thtl-contributions-heading">Key Contributions</li>
            {KEY_CONTRIBUTIONS.map((c, i) => (
              <li key={i} className="thtl-contribution">
                <span className="thtl-contribution-dot" aria-hidden="true" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Visual World — image gallery */}
      <section
        id="visual-world"
        className="thtl-section reveal"
        data-testid="mnnk-visual-world"
      >
        <div className="thtl-section-header">
          <span className="section-number">03</span>
          <h2 className="thtl-section-title">Visual World</h2>
          <span className="thtl-section-meta">
            Editorial &amp; social campaign assets
          </span>
        </div>

        <div className="thtl-process-copy mnnk-world-copy">
          <p>
            The visual language combined glossy futurism, surreal toy-like
            forms, chrome architecture, oversized flower characters, diamond
            surfaces, motorcycle imagery, and portal-like environments — built
            to feel like a complete alternate world rather than a set of
            disconnected AI images.
          </p>
        </div>

        <div className="mnnk-gallery" data-testid="mnnk-gallery">
          {complexMnnkBroMedia.gallery.map((item, i) => (
            <GalleryItem key={i} item={item} />
          ))}
        </div>
      </section>

      {/* Motion Assets */}
      <section
        id="motion"
        className="thtl-section reveal"
        data-testid="mnnk-motion"
      >
        <div className="thtl-section-header">
          <span className="section-number">04</span>
          <h2 className="thtl-section-title">Motion Assets</h2>
          <span className="thtl-section-meta">3 AI-generated pieces</span>
        </div>

        <div className="thtl-process-copy mnnk-world-copy">
          <p>
            Three AI-generated motion pieces extended the world beyond still
            imagery — turning the magazine cover into a living, looping artifact
            and pulling the editorial carousel into atmosphere and movement.
          </p>
        </div>

        {/* Cover motion — marquee */}
        <InlineVideo video={complexMnnkBroMedia.motion.cover} big />

        {/* Carousel motion pair */}
        <div
          className="mnnk-carousel-motion-grid"
          data-testid="mnnk-carousel-motion-grid"
        >
          {complexMnnkBroMedia.motion.carousel.map((v, i) => (
            <InlineVideo key={i} video={v} />
          ))}
        </div>
      </section>

      {/* Vinyl Edition */}
      <section
        id="vinyl"
        className="thtl-section mnnk-vinyl-section reveal"
        data-testid="mnnk-vinyl"
      >
        <div className="thtl-section-header">
          <span className="section-number">05</span>
          <h2 className="thtl-section-title">Vinyl Edition</h2>
          <span className="thtl-section-meta">
            Physical product · First vinyl artwork
          </span>
        </div>

        <div className="mnnk-vinyl-grid">
          <figure
            className="mnnk-vinyl-art"
            data-testid="mnnk-vinyl-art"
          >
            <img
              src={complexMnnkBroMedia.vinyl.src}
              alt={complexMnnkBroMedia.vinyl.alt}
              loading="lazy"
            />
          </figure>
          <div className="mnnk-vinyl-copy">
            <p>
              The cover art I created for the campaign was carried through
              into a physical vinyl edition for the MNNK Bro. release.
              Working alongside Takashi Murakami's team and the Complex
              design team, we adapted the cover into a full vinyl package —
              sleeve, Japanese title treatment, and a die-cut flower-shaped
              record pressed in pink, yellow, and orange.
            </p>
            <p className="mnnk-vinyl-highlight">
              <span className="thtl-results-tag">First vinyl artwork.</span>
              A personal milestone — my first vinyl album cover, taken from a
              campaign image into a physical artifact people can hold.
            </p>
          </div>
        </div>
      </section>

      {/* Outcome */}
      <section
        id="outcome"
        className="thtl-section thtl-results reveal"
        data-testid="mnnk-outcome"
      >
        <div className="thtl-section-header">
          <span className="section-number">06</span>
          <h2 className="thtl-section-title">Outcome</h2>
        </div>

        <div className="thtl-results-body">
          <p className="thtl-results-line">
            <span className="thtl-results-tag">A complete visual world.</span>{" "}
            Supplied artwork and brand assets were transformed into a larger
            cinematic environment system that could carry editorial covers,
            social carousels, and motion assets without breaking continuity.
          </p>
          <p className="thtl-results-line">
            <span className="thtl-results-tag">Editorial-grade AI.</span> Every
            frame had to feel imaginative and AI-native, yet polished enough to
            sit inside a Complex editorial and social rollout — glossy
            futurism, Murakami-inspired surrealism, streetwear energy, and
            Complex's visual tone all balanced in one system.
          </p>
          <p className="thtl-results-line">
            <span className="thtl-results-tag">Static into motion.</span> Three
            AI-generated videos extended the campaign beyond stills, turning
            the magazine cover and editorial carousel into living, looping
            pieces with movement, scale, and atmosphere.
          </p>
        </div>
      </section>

      {/* Outro */}
      <section className="thtl-outro reveal" data-testid="mnnk-outro">
        <div className="thtl-outro-inner">
          <h3 className="thtl-outro-title">
            Building an AI-generated worldbuilding system, editorial campaign,
            or motion package?
          </h3>
          <div className="thtl-outro-actions">
            <a
              href="mailto:dbmcnamara@gmail.com"
              className="thtl-outro-email"
              data-testid="mnnk-outro-email"
            >
              dbmcnamara@gmail.com
            </a>
            <Link
              to="/"
              className="thtl-outro-back"
              data-testid="mnnk-outro-back-link"
            >
              ← Back to Work
            </Link>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <p>© 2026 Dan McNamara. Available for full-time and freelance work.</p>
      </footer>
    </div>
  );
}
